/**
 * Layout Shell Component
 * Global wrapper that provides consistent page structure with Header and Footer
 */

import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { LayoutPayload } from "@/types/layout";
import type { LocaleOptions } from "@/api/locale";
import { useLocalizedTexts, t } from "@/utils/translations";
import { useCart } from "@/hooks/useCart";
import { getLocale } from "@/utils/locale-store";
import { readSearchEntry } from "@/utils/searchEntry";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { CartDrawer } from "@/components/layout/CartDrawer";
import { LocaleSelectorModal } from "@/components/layout/LocaleSelectorModal";
import styles from "./Layout.module.css";

interface LayoutProps {
  children: ReactNode;
  layoutData: LayoutPayload;
  localeOptions: LocaleOptions;
}

/**
 * A search another app asked for (`?q=` on arrival, see utils/searchEntry).
 * Boxed so that the same term twice in a row still reads as a new request.
 */
export interface SearchEntryRequest {
  query: string;
}

export function Layout({ children, layoutData, localeOptions }: LayoutProps) {
  const { cartItemCount, toggleDrawer, isDrawerOpen } = useCart();
  const localizedTexts = useLocalizedTexts();
  const [isLocaleSelectorOpen, setLocaleSelectorOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchEntry, setSearchEntry] = useState<SearchEntryRequest | null>(
    null
  );

  // Consume `?q=` from the URL: hand the term to the header, which opens the
  // search overlay with it, and strip the parameter so a reload does not open
  // it again. Through the router rather than history.replaceState so every
  // useLocation() below sees the stripped address at once.
  useEffect(() => {
    const entry = readSearchEntry(location.search);
    if (entry.search === location.search) return;
    if (entry.query) setSearchEntry({ query: entry.query });
    navigate(
      {
        pathname: location.pathname,
        search: entry.search,
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.search, location.pathname, location.hash, navigate]);

  const locale = getLocale();
  const { menu, topbar, footer, pages, isUserAuthenticated, userType } =
    layoutData;

  return (
    <div className={styles.layout}>
      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="skip-link">
        {t(
          localizedTexts,
          "Tracker.Generic.SkipToMainContent",
          "Skip to main content"
        )}
      </a>

      <Header
        menu={menu}
        topbar={topbar}
        pages={pages}
        localizedTexts={localizedTexts}
        isUserAuthenticated={isUserAuthenticated}
        userType={userType}
        currentLocale={locale.language}
        cartItemCount={cartItemCount}
        currentCountryCode={locale.country}
        localeOptions={localeOptions}
        onCountrySelectorClick={() => setLocaleSelectorOpen(true)}
        onCartToggle={toggleDrawer}
        isCartDrawerOpen={isDrawerOpen}
        cartDrawer={<CartDrawer />}
        searchEntry={searchEntry}
      />

      <main id="main-content" className={styles.main}>
        <div className={styles.mainContent}>{children}</div>
      </main>

      <Footer
        footer={footer}
        localizedTexts={localizedTexts}
        currentLocale={locale.language}
        currentCountry={locale.country}
        localeOptions={localeOptions}
        onLocationSelectorClick={() => setLocaleSelectorOpen(true)}
      />

      <LocaleSelectorModal
        isOpen={isLocaleSelectorOpen}
        onClose={() => setLocaleSelectorOpen(false)}
        localeOptions={localeOptions}
        currentLanguage={locale.language}
        currentCountry={locale.country}
      />
    </div>
  );
}

// Loading state component
export function LayoutLoading() {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingSpinner} aria-label="Loading..." />
    </div>
  );
}

// Error state component
interface LayoutErrorProps {
  error?: string;
  onRetry?: () => void;
}

export function LayoutError({ error, onRetry }: LayoutErrorProps) {
  const handleReset = () => {
    // Clear locale preferences
    try {
      localStorage.removeItem("wehunt-locale");
    } catch {
      // localStorage unavailable
    }
    // Clear commerce cookies by expiring them
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0]?.trim() ?? "";
      if (name.startsWith("umbracoCommerce")) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
    // Redirect to English homepage
    window.location.href = "/en/";
  };

  return (
    <div className={styles.errorContainer}>
      <h1 className={styles.errorTitle}>Oops! Something went wrong</h1>
      <p className={styles.errorMessage}>
        {error ||
          "We are unable to load the page right now. Please try again in a few moments."}
      </p>
      <div className={styles.errorActions}>
        {onRetry && (
          <button
            type="button"
            className={styles.errorRetryButton}
            onClick={onRetry}
          >
            Try Again
          </button>
        )}
        <button
          type="button"
          className={styles.errorResetButton}
          onClick={handleReset}
        >
          Go to English homepage
        </button>
      </div>
    </div>
  );
}

export default Layout;
